import { FC } from "react";
import { FieldArray, useFormikContext } from "formik";
import { FieldGroup } from "@blanket/components/layout/FieldGroup";
import FormSection from "@blanket/components/formSections/FormSection";
import SubGroupPanel from "@blanket/components/formSections/SubGroupPanel";
import Button from "@blanket/components/buttons/Button";
import TooltippedRemoveButton from "@blanket/components/buttons/TooltippedRemoveButton";
import type { CompanyRegistrationValues } from "../model/registrationTypes";
import {
  ChangeAdvokatselskabKind,
  InterestPartyChangeDirection,
  JaNej,
} from "../model/registrationEnums";
import { emptyChangeAdvokatselskabPartner } from "../model/registrationInitialValues";
import {
  SelectFormField,
  TextFormField,
} from "@blanket/components/formFields/FormFields";
import { ChangeAdvokatselskabEnteringPartnerEligibilityFields } from "../components/eligibility/ChangeAdvokatselskabEnteringPartnerEligibilityFields";
import { LeavingPartyFields } from "../components/eligibility/LeavingPartyFields";
import { useCompanyNameLookup } from "@blanket/hooks/useCompanyNameLookup";
import { CompanyNameSuggestions } from "../components/CompanyNameSuggestions";
import { RegistrationDateDisclosureNotice } from "../components/RegistrationDateDisclosureNotice";
import { RegistrationRemarksField } from "../components/RegistrationRemarksField";
import { RegistrationContactFields } from "../components/RegistrationContactFields";

export const ChangeAdvokatselskabSection: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const changeAdvokatselskab = values.changeAdvokatselskab;
  const nameLookup = useCompanyNameLookup({
    name: "changeAdvokatselskab.name",
    cvr: "changeAdvokatselskab.cvr",
    pNumber: "changeAdvokatselskab.pNumber",
  });
  const showPartnerDirection =
    changeAdvokatselskab.changeKind === ChangeAdvokatselskabKind.PartnerBegge;

  return (
    <>
      <FormSection
        id="section-change-co-info"
        number={2}
        title="Stamoplysninger"
        description="Stamoplysninger om virksomheden, der ændres."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyNameSuggestions
            items={nameLookup.data}
            isLoading={nameLookup.isLoading}
            onSelect={nameLookup.select}
            onDismiss={nameLookup.dismiss}
          >
            <TextFormField
              name="changeAdvokatselskab.name"
              label="Navn på virksomhed"
              id="change-co-input-name"
              isRequired
            />
          </CompanyNameSuggestions>
          <div className="blanket-two-col">
            <TextFormField
              name="changeAdvokatselskab.cvr"
              label="CVR-nr."
              id="change-co-input-cvr"
              isRequired
              type="number"
            />
            <TextFormField
              name="changeAdvokatselskab.pNumber"
              label="P-nummer"
              id="change-co-input-p-number"
              isRequired
              type="number"
            />
          </div>
        </div>
      </FormSection>

      <FormSection
        id="section-change-co-details"
        number={3}
        title="Detaljer om ændringen"
        description="Type af ændring og deltageroplysninger."
      >
        <div className="d-flex flex-column gap-4">
          <SelectFormField
            name="changeAdvokatselskab.changeKind"
            label="Hvilken type ændring sker der?"
            isRequired
            id="change-co-input-change-kind"
            options={[
              {
                value: ChangeAdvokatselskabKind.PartnerInd,
                label: ChangeAdvokatselskabKind.PartnerInd,
              },
              {
                value: ChangeAdvokatselskabKind.PartnerUd,
                label: ChangeAdvokatselskabKind.PartnerUd,
              },
              {
                value: ChangeAdvokatselskabKind.PartnerBegge,
                label: ChangeAdvokatselskabKind.PartnerBegge,
              },
            ]}
          />
          <SubGroupPanel
            className="blanket-participant-subsection"
            title="Oplysninger om selskabsdeltageren"
          >
            <FieldArray name="changeAdvokatselskab.partners">
              {({ push, remove }) => (
                <FieldGroup className="blanket-participant-list">
                  {changeAdvokatselskab.partners.map((partner, index) => {
                    const isEnters =
                      changeAdvokatselskab.changeKind ===
                        ChangeAdvokatselskabKind.PartnerInd ||
                      (showPartnerDirection &&
                        partner.direction ===
                          InterestPartyChangeDirection.Enters);
                    const isLeaves =
                      changeAdvokatselskab.changeKind ===
                        ChangeAdvokatselskabKind.PartnerUd ||
                      (showPartnerDirection &&
                        partner.direction ===
                          InterestPartyChangeDirection.Leaves);

                    return (
                      <div
                        key={partner._rowId}
                        className="blanket-participant-card"
                      >
                        <div className="d-flex flex-column gap-4">
                          {showPartnerDirection && (
                            <SelectFormField
                              name={`changeAdvokatselskab.partners.${index}.direction`}
                              label="Træder deltageren ind eller ud?"
                              id={`change-co-partner-direction-${index}`}
                              isRequired
                              options={[
                                {
                                  value: InterestPartyChangeDirection.Enters,
                                  label: "Ind",
                                },
                                {
                                  value: InterestPartyChangeDirection.Leaves,
                                  label: "Ud",
                                },
                              ]}
                            />
                          )}
                          <div className="blanket-two-col">
                            <TextFormField
                              name={`changeAdvokatselskab.partners.${index}.name`}
                              label="Navn"
                              id={`change-co-partner-name-${index}`}
                              isRequired
                            />
                            <TextFormField
                              name={`changeAdvokatselskab.partners.${index}.cvrCpr`}
                              label="CVR/CPR"
                              id={`change-co-partner-cvr-${index}`}
                              isRequired
                            />
                          </div>
                          {isLeaves && (
                            <LeavingPartyFields
                              namePrefix={`changeAdvokatselskab.partners.${index}`}
                              idPrefix={`change-co-partner-${index}`}
                              participantType={partner.participantType}
                              withdrawalReason={partner.withdrawalReason}
                            />
                          )}
                          {isEnters && (
                            <ChangeAdvokatselskabEnteringPartnerEligibilityFields
                              namePrefix={`changeAdvokatselskab.partners.${index}`}
                              idPrefix={`change-co-partner-${index}`}
                              isLawyerOrLawFirm={partner.isLawyerOrLawFirm}
                              passedPartnerExam={partner.passedPartnerExam}
                              leavesOtherOnEntry={partner.leavesOtherOnEntry}
                            />
                          )}
                          {isEnters &&
                            partner.leavesOtherOnEntry === JaNej.Ja && (
                              <>
                                <TextFormField
                                  name={`changeAdvokatselskab.partners.${index}.otherCompanyName`}
                                  label="Navn på virksomhed"
                                  id={`change-co-partner-other-company-name-${index}`}
                                />
                                <TextFormField
                                  name={`changeAdvokatselskab.partners.${index}.otherCompanyCvr`}
                                  label="CVR-nr."
                                  id={`change-co-partner-other-company-cvr-${index}`}
                                />
                              </>
                            )}
                        </div>
                        <div className="mt-3 d-flex justify-content-end">
                          <TooltippedRemoveButton
                            onClick={() => remove(index)}
                            ariaLabel="Fjern"
                            disabled={changeAdvokatselskab.partners.length <= 1}
                            tooltipContent="Mindst én skal angives"
                          >
                            Fjern
                          </TooltippedRemoveButton>
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    fullWidth={false}
                    onClick={() => push(emptyChangeAdvokatselskabPartner())}
                    aria-label="Tilføj selskabsdeltager"
                  >
                    Tilføj selskabsdeltager
                  </Button>
                </FieldGroup>
              )}
            </FieldArray>
          </SubGroupPanel>
        </div>
      </FormSection>

      <FormSection id="section-change-co-date" number={4} title="Ændringsdato">
        <div className="mb-4">
          <RegistrationRemarksField
            namePrefix="changeAdvokatselskab"
            idPrefix="change-co"
          />
        </div>
        <TextFormField
          name="changeAdvokatselskab.changeDate"
          label="Ændringsdato"
          isRequired
          id="change-co-input-change-date"
          type="date"
        />
        <RegistrationDateDisclosureNotice />
        <div className="blanket-two-col mt-4">
          <RegistrationContactFields
            namePrefix="changeAdvokatselskab"
            idPrefix="change-co"
          />
        </div>
      </FormSection>
    </>
  );
};
